import { Outlet, Link, useLocation } from 'react-router-dom';
import { User } from 'lucide-react';
import { UnlimitIcon, UnlimitWordmark } from './UnlimitLogo';

export default function Layout() {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-unlimit-gray-light flex flex-col">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2.5 group">
              <UnlimitIcon size={32} className="group-hover:scale-105 transition-transform" />
              <div className="flex items-baseline space-x-1.5">
                <UnlimitWordmark className="h-4 text-navy-900" />
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-widest">Status</span>
              </div>
            </Link>
            <nav className="flex items-center space-x-1">
              {[
                { path: '/', label: 'Status' },
                { path: '/availability', label: 'Availability' },
                { path: '/history', label: 'History' },
              ].map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(path)
                      ? 'text-navy-900 bg-unlimit-gray-light'
                      : 'text-gray-500 hover:text-navy-900 hover:bg-gray-50'
                  }`}
                >
                  {label}
                  {isActive(path) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-unlimit-lime rounded-full" />
                  )}
                </Link>
              ))}
              <div className="w-px h-5 bg-gray-200 mx-3" />
              <Link
                to="/console"
                className="w-9 h-9 flex items-center justify-center bg-navy-900 rounded-full hover:bg-unlimit-lime transition-colors group/login"
                title="Admin Console"
              >
                <User className="h-4 w-4 text-white group-hover/login:text-navy-900 transition-colors" strokeWidth={2} />
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full animate-fade-in">
        <Outlet />
      </main>

      <footer className="bg-navy-900 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2.5 mb-4">
                <UnlimitIcon size={28} />
                <UnlimitWordmark className="h-3.5 text-white" />
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Real-time monitoring and status updates for all Unlimit services and infrastructure.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Pages</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/" className="text-sm text-gray-300 hover:text-unlimit-lime transition-colors">
                    System Status
                  </Link>
                </li>
                <li>
                  <Link to="/availability" className="text-sm text-gray-300 hover:text-unlimit-lime transition-colors">
                    Availability
                  </Link>
                </li>
                <li>
                  <Link to="/history" className="text-sm text-gray-300 hover:text-unlimit-lime transition-colors">
                    Incident History
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-2.5">
                <li>
                  <a href="https://www.unlimit.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-unlimit-lime transition-colors">
                    About Unlimit
                  </a>
                </li>
                <li>
                  <a href="https://www.unlimit.com/contact" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-unlimit-lime transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs text-gray-500">
              &copy; {new Date().getFullYear()} Unlimit. All rights reserved.
            </p>
            <div className="flex items-center space-x-1.5">
              <span className="inline-block w-1.5 h-1.5 bg-operational rounded-full animate-pulse" />
              <span className="text-xs text-gray-500">All systems operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
